import { peekVariant, resolveVariant, trackExposure, TEST } from './experimentAssignment';

/**
 * The /interview funnel comes in two shapes.
 *
 * Control is the funnel as it stands: it opens with the Decision question and
 * shows the full nav menu. The extended flow drops that opening question,
 * hides the menu, and gates entry with its own qualifying question — "Do you
 * already know which GLP-1 is right for you?" — before running bestyou
 * through priority, the same three questions control asks.
 *
 * V2 (interview-v2) added three more questions to that route (diet, movement,
 * glp1-history) and lost. V2.1 (interview-v2-1) kept those three but added
 * this gate in front — also lost. V2.2 tests the gate in isolation: same
 * three questions as control, reached through the gate instead of Decision.
 * The InterviewDiet/Movement/Glp1History components and their routes are
 * left in place, unreferenced by either flow, in case a future iteration
 * revisits them.
 *
 * A visitor is only ever enrolled by startInterview(), which a landing page CTA
 * calls on its way into the funnel. Every other caller reads the assignment
 * without creating one, so somebody who opens /interview directly is never
 * counted in the experiment and always sees control.
 */

export const FLAG_KEY = 'interview-v2-2';

// Share of landing-page submitters entering the experiment at all. Keep this in
// sync with the flag's rollout in PostHog, since a visitor whose flags are
// already cached is assigned by the flag rather than by the local coin flip.
const ENROLLED_SHARE = 1.0;

// Share of *enrolled* visitors who see the extended funnel.
const TEST_SHARE = 0.50;

export const CONTROL_FLOW = {
    id: 'control',
    hideNavigation: false,
    entryPath: '/interview',
    steps: [
        '/interview',
        '/interview/bestyou',
        '/interview/height-weight',
        '/interview/priority'
    ]
};

export const EXTENDED_FLOW = {
    id: 'extended',
    hideNavigation: true,
    // Where a landing CTA sends a newly-enrolled visitor. Deliberately not
    // steps[0]: the gate decides whether the visitor enters this route at
    // all, the same way Decision decides entry into control, so it is not
    // one of the three questions and would renumber them if folded into
    // steps.
    entryPath: '/interview/glp1-known',
    steps: [
        '/interview/bestyou',
        '/interview/height-weight',
        '/interview/priority'
    ]
};

/**
 * Enroll this visitor and report the exposure. This is the only place an
 * assignment is created. Call it from a landing CTA immediately before the
 * submit event is tracked, so the variant is registered on the person first and
 * that event carries it.
 *
 * @returns {{ id: string, hideNavigation: boolean, entryPath: string, steps: string[] }}
 *          the flow this visitor should follow; navigate to its entryPath
 */
export function startInterview() {
    const { variant } = resolveVariant({
        flagKey: FLAG_KEY,
        enrolledShare: ENROLLED_SHARE,
        testShare: TEST_SHARE
    });

    trackExposure(FLAG_KEY, variant);

    return variant === TEST ? EXTENDED_FLOW : CONTROL_FLOW;
}

/**
 * Read this visitor's flow without enrolling them. Anyone who did not arrive
 * through a landing CTA has no assignment and gets the control funnel.
 */
export function getInterviewFlow() {
    return peekVariant(FLAG_KEY).variant === TEST ? EXTENDED_FLOW : CONTROL_FLOW;
}

// The three helpers below are total: a path that is not part of the given flow
// returns a safe value rather than undefined. navigate(undefined) is read by
// react-router as navigate(0), which reloads the page — a loop with no exit.

export function stepPosition(flow, path) {
    const index = flow.steps.indexOf(path);
    return index === -1 ? 1 : index + 1;
}

export function nextStep(flow, path) {
    const index = flow.steps.indexOf(path);
    return index === -1 ? null : flow.steps[index + 1] ?? null;
}

export function previousStep(flow, path) {
    const index = flow.steps.indexOf(path);
    return index <= 0 ? null : flow.steps[index - 1];
}
